/**
 * Pure utility functions for calculating contact status and days since last contact.
 * No React dependencies - these are pure JavaScript functions.
 */

/**
 * Calculate days since last contact, handling Firestore Timestamps and string dates
 * @param {Date|string|FirestoreTimestamp} lastContactDate - The last contact date
 * @returns {number} Days since last contact (0 if today, 999 if never contacted)
 */
export const getDaysSinceLastContact = (lastContactDate) => {
  if (!lastContactDate) return 999; // Very high number for never contacted
  
  // Handle Firestore Timestamp objects
  let last;
  if (lastContactDate.toDate) {
    last = lastContactDate.toDate();
  } else {
    last = new Date(lastContactDate);
  }
  
  if (isNaN(last.getTime())) return 999;
  
  // Compare dates at day level (ignore time) to ensure same day = 0 days
  // Use UTC to avoid timezone issues
  const now = new Date();
  const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const lastDayUTC = Date.UTC(last.getUTCFullYear(), last.getUTCMonth(), last.getUTCDate());
  
  const diffTime = todayUTC - lastDayUTC;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  // If date is in the future (negative days), return 0 (treat as today)
  return diffDays < 0 ? 0 : diffDays;
};

/**
 * Get contact status based on days since last contact and contact frequency
 * @param {Object} friend - Friend object with lastContactDate and contactFrequency
 * @param {Function} t - Translation function (optional, for messages)
 * @returns {Object} Status object with status, color, and message
 */
export const getContactStatus = (friend, t = null) => {
  const daysSince = getDaysSinceLastContact(friend.lastContactDate);
  const frequencyMap = {
    'daily': 1,
    'weekly': 7,
    'biweekly': 14,
    'monthly': 30,
    'quarterly': 90
  };
  const targetDays = frequencyMap[friend.contactFrequency] || 14;
  
  if (daysSince <= targetDays) {
    return { 
      status: 'healthy', 
      color: '#10B981', 
      message: t ? t('allGoodStatus') : 'All good!' 
    };
  } else if (daysSince <= targetDays * 1.5) {
    return { 
      status: 'attention', 
      color: '#F59E0B', 
      message: t ? t('timeToReachOut') : 'Time to reach out' 
    };
  } else {
    return { 
      status: 'urgent', 
      color: '#EF4444', 
      message: t ? t('needsAttentionStatus') : 'Needs attention' 
    };
  }
};
